import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, ShieldCheck, UserCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { EmptyState } from "@/components/empty-state";
import { StoryCard, StoryCardSkeleton } from "@/components/story-card";
import { TipWriterDialog } from "@/components/tip-writer-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { MembersOnlyGate } from "@/components/members-only-gate";
import { useAuth } from "@/lib/auth-context";
import { fetchAuthorStories, fetchProfileByUsername, fetchProfileStats } from "@/lib/queries";
import { cn } from "@/lib/utils";

const SITE_URL = "https://quibblies-inkwell.lovable.app";

export const Route = createFileRoute("/u/$username")({
  head: ({ params }) => {
    const url = `${SITE_URL}/u/${encodeURIComponent(params.username)}`;
    return {
      meta: [
        { title: `@${params.username} — Quibblies` },
        {
          name: "description",
          content: `Read published writing by @${params.username} on Quibblies and follow their new publications.`,
        },
        { property: "og:title", content: `@${params.username} on Quibblies` },
        {
          property: "og:description",
          content: `Writing published by @${params.username}.`,
        },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            url,
            mainEntity: {
              "@type": "Person",
              name: params.username,
              alternateName: `@${params.username}`,
              url,
            },
          }),
        },
      ],
    };
  },
  component: ProfilePage,
});

const TABS = [
  { value: "writing", label: "Writing" },
  { value: "series", label: "Series" },
  { value: "about", label: "About" },
] as const;

function ProfilePage() {
  const { username } = Route.useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = React.useState<(typeof TABS)[number]["value"]>("writing");

  const profile = useQuery({
    queryKey: ["profile", username],
    queryFn: () => fetchProfileByUsername(username),
  });
  const userId = profile.data?.id;

  const relationship = useQuery({
    queryKey: ["profile-follow", user?.id, userId],
    queryFn: async () => {
      if (!user || !userId) return false;
      const { data, error } = await supabase
        .from("follows")
        .select("following_id")
        .eq("follower_id", user.id)
        .eq("following_id", userId)
        .maybeSingle();
      if (error) throw error;
      return Boolean(data);
    },
    enabled: Boolean(user && userId && user.id !== userId),
  });

  const toggleFollow = useMutation({
    mutationFn: async () => {
      if (!user || !userId) throw new Error("Sign in to follow writers");
      const query = supabase.from("follows");
      const { error } = relationship.data
        ? await query.delete().eq("follower_id", user.id).eq("following_id", userId)
        : await query.insert({ follower_id: user.id, following_id: userId });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(relationship.data ? "Writer unfollowed" : "Writer followed");
      void queryClient.invalidateQueries({ queryKey: ["profile-follow", user?.id, userId] });
      void queryClient.invalidateQueries({ queryKey: ["profile-stats", userId] });
      void queryClient.invalidateQueries({ queryKey: ["following-feed"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const stats = useQuery({
    queryKey: ["profile-stats", userId],
    queryFn: () => fetchProfileStats(userId!),
    enabled: Boolean(userId),
  });
  const stories = useQuery({
    queryKey: ["author-stories", userId],
    queryFn: () => fetchAuthorStories(userId!),
    enabled: Boolean(userId),
  });

  if (!user) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
        <MembersOnlyGate
          title="Profiles are for members"
          description="Sign in to view member profiles and the writing they have published."
        />
      </div>
    );
  }

  if (profile.isLoading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <Skeleton className="h-40 rounded-3xl" />
      </div>
    );
  }

  if (!profile.data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
        <EmptyState
          icon={BookOpen}
          title="Profile not found"
          description={`No writer with the username @${username}.`}
        />
      </div>
    );
  }

  const person = profile.data;
  const allStories = stories.data ?? [];
  const visible =
    tab === "series"
      ? allStories.filter(
          (story) => story.content_types?.slug === "series" || story.story_type === "series",
        )
      : allStories;
  const counts = [
    { label: "Writing", value: stats.data?.published ?? 0 },
    { label: "Followers", value: stats.data?.followers ?? 0 },
    { label: "Following", value: stats.data?.following ?? 0 },
    { label: "Likes", value: stats.data?.likes ?? 0 },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <header className="rounded-[2rem] border border-border/70 bg-gradient-card p-8">
        <div className="flex flex-wrap items-center gap-5">
        <div className="flex flex-col items-center gap-2">
            <span className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-accent font-display text-2xl text-primary-glow">
              {person.avatar_url ? (
                <img src={person.avatar_url} alt="" className="h-full w-full object-cover" />
              ) : (
                person.display_name.slice(0, 2).toUpperCase()
              )}
            </span>
            {person.age_verified ? (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary"
                title="This member's ID has been verified by the Quibblies team"
              >
                <ShieldCheck aria-hidden="true" className="h-3 w-3" />
                Age verified
              </span>
            ) : null}
          </div>
          <div>
            <h1 className="font-display text-3xl text-foreground">{person.display_name}</h1>
            <p className="text-sm text-muted-foreground">@{person.username}</p>
          </div>
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            {user && user.id !== person.id ? (
              <Button
                variant={relationship.data ? "soft" : "hero"}
                size="pill"
                disabled={toggleFollow.isPending || relationship.isLoading}
                onClick={() => toggleFollow.mutate()}
              >
                {relationship.data ? <UserCheck aria-hidden="true" /> : <UserPlus aria-hidden="true" />}
                {relationship.data ? "Following" : "Follow"}
              </Button>
            ) : null}
            {person.support_enabled ? (
              <TipWriterDialog writerId={person.id} writerName={person.display_name} />
            ) : null}
          </div>
        </div>
        {person.bio ? (
          <p className="mt-5 max-w-2xl leading-relaxed text-muted-foreground">{person.bio}</p>
        ) : null}
        <dl className="mt-6 flex flex-wrap gap-8">
          {counts.map((item) => (
            <div key={item.label}>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {item.label}
              </dt>
              <dd className="font-display text-xl text-foreground">{item.value}</dd>
            </div>
          ))}
        </dl>
      </header>

      <section className="mt-10">
        <div className="flex flex-wrap gap-2">
          {TABS.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setTab(item.value)}
              className={cn(
                "rounded-full border border-border/70 px-4 py-1.5 text-sm transition-smooth",
                tab === item.value
                  ? "border-primary/60 bg-accent text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="mt-5">
          {tab === "about" ? (
            <div className="rounded-2xl border border-border/70 bg-surface/50 p-6 text-sm leading-relaxed text-muted-foreground">
              {person.bio ?? "This writer hasn't added an About yet."}
            </div>
          ) : stories.isLoading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <StoryCardSkeleton key={index} />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <EmptyState
              icon={BookOpen}
              title="Nothing published yet"
              description="When this writer's work clears review, it will appear here."
            />
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((story) => (
                <StoryCard key={story.id} story={story} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
